import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  Grid,
  RefreshCcw,
  CheckCircle,
  Clock,
  TrendingUp,
  X,
  XCircle,
  AlertTriangle,
  UserCheck,
  UserX,
  Paperclip
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { CustomSelect, CustomDatePicker } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { formatPointageDate, formatPointageMoney, calculateAbsencePenalty, SAUDI_DEFAULT_TIMETRACKING_CONFIG } from './pointageHelpers';

const NS = 'modules.pointage.pages.absencesPage';

const DEFAULT_ABSENCE_TYPE_KEYS = ['paid_leave', 'sick_leave', 'unpaid_leave', 'maternity_leave', 'unjustified_absence'];

const DEFAULT_ABSENCE_TYPE_VALUES = {
  paid_leave: 'Annual Leave',
  sick_leave: 'Sick Leave',
  unpaid_leave: 'Unpaid Leave',
  maternity_leave: 'Maternity Leave',
  unjustified_absence: 'Unjustified Absence',
};

/** Legacy French values stored in older records. */
const LEGACY_ABSENCE_TYPE_MAP = {
  'Congé payé': DEFAULT_ABSENCE_TYPE_VALUES.paid_leave,
  'Congé maladie': DEFAULT_ABSENCE_TYPE_VALUES.sick_leave,
  'Congé sans solde': DEFAULT_ABSENCE_TYPE_VALUES.unpaid_leave,
  'Absence non justifiée': DEFAULT_ABSENCE_TYPE_VALUES.unjustified_absence,
};

const AbsencesPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`${NS}.${key}`, opts);

  const DEFAULT_ABSENCE_TYPES = Object.values(DEFAULT_ABSENCE_TYPE_VALUES);
  const getAbsenceTypeLabel = (type) => {
    const normalized = LEGACY_ABSENCE_TYPE_MAP[type] || type;
    const entry = Object.entries(DEFAULT_ABSENCE_TYPE_VALUES).find(([, value]) => value === normalized);
    if (entry) return p(`default_types.${entry[0]}`);
    return type;
  };
  
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Employee options — live HR (/hr/employees), id-based linkage
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );
  
  // Dynamic Type d'absence options (3 in 1: Fetch + Search + Create)
  const [absenceTypeOptions, setAbsenceTypeOptions] = useState([]);
  
  // Dynamic Status options (3 in 1: Fetch + Search + Create)
  const [statusOptions, setStatusOptions] = useState([]);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);

  const [formData, setFormData] = useState({
    employeeName: '',
    employeeId: null,
    type: '',
    startDate: '',
    endDate: '',
    reason: '',
    status: '',
    duration: 0,
    attachmentsNotes: '',
  });
  
  // Filters
  const [currentView, setCurrentView] = useState('all'); // all, pending, approved, rejected
  const [quickFilter, setQuickFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilterSelect, setStatusFilterSelect] = useState('all');
  
  // Data
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    totalDays: 0
  });

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => {
    fetchAbsences();
    fetchStats();
    fetchAbsenceTypes();
    fetchStatuses();
  }, []);

  // Fetch unique absence types from database (3 in 1: Fetch + Search + Create)
  const fetchAbsenceTypes = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/absences');
      if (data && data.length > 0) {
        // Extract unique types from existing absences
        const uniqueTypes = [...new Set(data.map(absence => absence.type).filter(type => type && type.trim() !== ''))];
        
        // Merge default types with existing types (remove duplicates)
        const allTypes = [...new Set([...DEFAULT_ABSENCE_TYPES, ...uniqueTypes])];
        
        // Separate user-created types from defaults
        const userCreatedTypes = allTypes.filter(type => !DEFAULT_ABSENCE_TYPES.includes(type));
        const defaultTypes = allTypes.filter(type => DEFAULT_ABSENCE_TYPES.includes(type));
        
        // Sort: User-created at TOP, then defaults
        const sortedTypes = [...userCreatedTypes, ...defaultTypes];
        
        // Convert to options format with isDefault flag
        const options = sortedTypes.map(type => ({
          value: type,
          label: getAbsenceTypeLabel(type),
          isDefault: DEFAULT_ABSENCE_TYPES.includes(type)
        }));
        
        setAbsenceTypeOptions(options);
      } else {
        // If no data, use default types
        setAbsenceTypeOptions(DEFAULT_ABSENCE_TYPES.map(type => ({ 
          value: type, 
          label: getAbsenceTypeLabel(type),
          isDefault: true 
        })));
      }
    } catch (error) {
      console.error('Error fetching absence types:', error);
      // Fallback to default types
      setAbsenceTypeOptions(DEFAULT_ABSENCE_TYPES.map(type => ({ 
        value: type, 
        label: getAbsenceTypeLabel(type),
        isDefault: true 
      })));
    }
  };

  // Fetch unique statuses from database (3 in 1: Fetch + Search + Create)
  const fetchStatuses = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/absences');
      if (data && data.length > 0) {
        // Extract unique statuses from existing absences
        const uniqueStatuses = [...new Set(data.map(absence => absence.status).filter(status => status && status.trim() !== ''))];
        
        // Default statuses to suggest
        const defaultStatuses = [
          { value: 'pending', label: p('status_pending') },
          { value: 'approved', label: p('status_approved') },
          { value: 'rejected', label: p('status_rejected') }
        ];
        
        // Merge default statuses with existing statuses (remove duplicates)
        const allStatuses = [...new Set([
          ...defaultStatuses.map(s => s.value),
          ...uniqueStatuses
        ])];
        
        // Convert to options format with labels
        const options = allStatuses.map(status => {
          const defaultStatus = defaultStatuses.find(s => s.value === status);
          return {
            value: status,
            label: defaultStatus ? defaultStatus.label : status
          };
        });
        
        setStatusOptions(options);
      } else {
        // If no data, use default statuses
        setStatusOptions([
          { value: 'pending', label: p('status_pending') },
          { value: 'approved', label: p('status_approved') },
          { value: 'rejected', label: p('status_rejected') }
        ]);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      // Fallback to default statuses
      setStatusOptions([
        { value: 'pending', label: p('status_pending') },
        { value: 'approved', label: p('status_approved') },
        { value: 'rejected', label: p('status_rejected') }
      ]);
    }
  };

  const fetchAbsences = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (statusFilterSelect && statusFilterSelect !== 'all') params.status = statusFilterSelect;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const data = await localApiAdapter.get('/timetracking/absences', params);
      setAbsences(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error fetching absences:', error);
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await localApiAdapter.get('/timetracking/absences/stats');
      setStats({
        total: data.total || 0,
        approved: data.approved || 0,
        pending: data.pending || 0,
        rejected: data.rejected || 0,
        totalDays: data.totalDays || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };


  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        employee_id: formData.employeeId,
        employee_name: formData.employeeName
          || resolvePointageEmployeeDisplayName(formData.employeeId, employeeOptions, ''),
        type: formData.type,
        start_date: formData.startDate,
        end_date: formData.endDate,
        reason: formData.reason,
        status: formData.status
      };

      if (selectedAbsence) {
        await localApiAdapter.put(`/timetracking/absences/${selectedAbsence.id}`, payload);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/timetracking/absences', payload);
        notifyCreated(showNotify, p('title'));
      }

      await fetchAbsences();
      await fetchStats();
      await fetchAbsenceTypes(); // Refresh dynamic types list
      await fetchStatuses(); // Refresh dynamic statuses list
      resetForm();
    } catch (error) {
      console.error('Error saving absence:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (absence) => {
    setSelectedAbsence(absence);
    setFormData({
      employeeName: absence.employee_name || absence.employeeName,
      employeeId: absence.employee_id != null ? String(absence.employee_id) : '',
      type: absence.type,
      startDate: absence.start_date || absence.startDate,
      endDate: absence.end_date || absence.endDate,
      reason: absence.reason || '',
      status: absence.status,
      duration: absence.duration,
      attachmentsNotes: absence.attachmentsNotes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/timetracking/absences/${id}`);
        notifyDeleted(showNotify, p('title'));
        await fetchAbsences();
        await fetchStats();
        await fetchAbsenceTypes(); // Refresh dynamic types list
        await fetchStatuses(); // Refresh dynamic statuses list
      } catch (error) {
        console.error('Error deleting absence:', error);
        alert(p('delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/timetracking/absences/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        await fetchAbsences();
        await fetchStats();
        await fetchAbsenceTypes();
        await fetchStatuses();
      } catch (error) {
        console.error('Error bulk deleting absences:', error);
        notifyError(showNotify, p('delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/timetracking/absences/${id}`, { status: newStatus });
      await fetchAbsences();
      await fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'rejected', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ], [p]);

  const statusColumnFilterOptions = useMemo(
    () => clickableStatusOptions.map(({ value, label }) => ({ value, label })),
    [clickableStatusOptions],
  );

  const handleDeleteAbsenceType = async (typeToDelete) => {
    if (window.confirm(p('delete_type_confirm', { type: typeToDelete }))) {
      try {
        setLoading(true);
        
        // Get all absences of this type
        const data = await localApiAdapter.get('/timetracking/absences');
        const absencesToDelete = data.filter(absence => absence.type === typeToDelete);
        
        // Delete all absences of this type
        await Promise.all(
          absencesToDelete.map(absence => 
            localApiAdapter.delete(`/timetracking/absences/${absence.id}`)
          )
        );
        
        // If the form is currently using this type, clear it
        if (formData.type === typeToDelete) {
          setFormData({ ...formData, type: '' });
        }
        
        // If current filter is the deleted type, reset to 'all'
        if (typeFilter === typeToDelete) {
          setTypeFilter('all');
        }
        
        // Refresh everything
        notifyBulkDeleted(showNotify, absencesToDelete.length, p('title'));
        await fetchAbsences();
        await fetchStats();
        await fetchAbsenceTypes();
      } catch (error) {
        console.error('Error deleting absence type:', error);
        notifyError(showNotify, p('delete_type_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (absence) => {
    setSelectedAbsence(absence);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ employeeName: '', employeeId: null, type: '', startDate: '', endDate: '', reason: '', status: '', duration: 0, attachmentsNotes: '' });
    setSelectedAbsence(null);
    setShowModal(false);
  };

  const getStatusFilterLabel = () => {
    if (statusFilterSelect === 'all') return p('filter_status');
    const labels = {
      approved: p('status_approved'),
      pending: p('status_pending'),
      rejected: p('status_rejected')
    };
    return labels[statusFilterSelect] || p('filter_status');
  };

  const formatDuration = (days) => p('duration_days', { count: days });

  const getAbsencePenaltyDisplay = (absence) => {
    if (absence.type !== DEFAULT_ABSENCE_TYPE_VALUES.unjustified_absence
      && LEGACY_ABSENCE_TYPE_MAP[absence.type] !== DEFAULT_ABSENCE_TYPE_VALUES.unjustified_absence) {
      return null;
    }
    const penalty = absence.penalty_amount ?? absence.penaltyAmount;
    if (penalty != null && penalty !== '') return formatPointageMoney(penalty);
    const dailyWage = absence.daily_wage ?? absence.dailyWage ?? 0;
    if (dailyWage > 0) {
      return formatPointageMoney(
        calculateAbsencePenalty(dailyWage, 1, SAUDI_DEFAULT_TIMETRACKING_CONFIG)
      );
    }
    return null;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'approved': 'bg-green-500/20 text-green-300 border-green-500/30',
      'pending': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'rejected': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    const labels = {
      'approved': p('status_approved'),
      'pending': p('status_pending'),
      'rejected': p('status_rejected')
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles['pending']}`}>
        {labels[status] || p('em_dash')}
      </span>
    );
  };

  const baseFilteredAbsences = useMemo(() => absences.filter(absence => {
    const matchesView = currentView === 'all' || absence.status === currentView;
    const matchesSearch = !searchTerm ||
      (absence.employee_name || absence.employeeName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      absence.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      absence.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesView && matchesSearch;
  }), [absences, currentView, searchTerm]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('employee_name', (a) => a.employee_name || a.employeeName || ''),
    textColumnRule('type', (a) => getAbsenceTypeLabel(a.type)),
    textColumnRule('start_date', (a) => formatPointageDate(a.start_date || a.startDate) || ''),
    textColumnRule('end_date', (a) => formatPointageDate(a.end_date || a.endDate) || ''),
    textColumnRule('duration', (a) => String(a.duration ?? a.duration_days ?? '')),
    textColumnRule('penalty', (a) => formatPointageMoney(calculateAbsencePenalty(a.duration || a.duration_days || 0))),
    exactColumnRule('status', (a) => a.status || ''),
  ], [p]);

  const filteredAbsences = useMemo(
    () => applyColumnFilters(baseFilteredAbsences, colFilters, columnFilterRules),
    [baseFilteredAbsences, colFilters, columnFilterRules],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredAbsences);

  const absenceKpiCards = [
    { title: p('kpi_total'), value: stats.total, icon: Calendar, color: 'from-blue-500 to-blue-600' },
    { title: p('kpi_approved'), value: stats.approved, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { title: p('kpi_pending'), value: stats.pending, icon: Clock, color: 'from-orange-500 to-orange-600' },
    { title: p('kpi_rejected'), value: stats.rejected, icon: XCircle, color: 'from-gray-500 to-gray-600' },
    { title: p('kpi_total_days'), value: stats.totalDays, icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
  ];

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'employee_name', labelKey: 'col_employee' },
    { key: 'type', labelKey: 'col_type' },
    { key: 'start_date', labelKey: 'col_start_date' },
    { key: 'end_date', labelKey: 'col_end_date' },
    { key: 'duration', labelKey: 'col_duration' },
    { key: 'penalty', labelKey: 'table_penalty' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  // ===== RENDER =====
  return (
    <div className="space-y-6 clients-container">
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {p('title')}
            <button onClick={() => { fetchAbsences(); fetchStats(); }} className="refresh-button ml-4" title={t('common.refresh')}>
              <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({
            count: selectedRecords.length,
            onDelete: handleBulkDelete,
            label: `${t('common.delete')} (${selectedRecords.length})`,
            title: t('common.delete', { defaultValue: 'حذف' }),
          })}
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={t('common.advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setSelectedAbsence(null);
              setFormData({
                employeeName: '',
                employeeId: null,
                type: '',
                startDate: '',
                endDate: '',
                reason: '',
                status: '',
                duration: 0,
                attachmentsNotes: ''
              });
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_absence')}</span>
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {absenceKpiCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group/card">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options - Collapsible */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Main View Tabs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  <button 
                    onClick={() => setCurrentView('all')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      currentView === 'all' 
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Grid className="h-5 w-5" />
                    <span>{t('common.all')}</span>
                  </button>
                  <button 
                    onClick={() => setCurrentView('pending')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      currentView === 'pending' 
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Clock className="h-5 w-5" />
                    <span>{p('status_pending')}</span>
                  </button>
                  <button 
                    onClick={() => setCurrentView('approved')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      currentView === 'approved' 
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <CheckCircle className="h-5 w-5" />
                    <span>{p('status_approved')}</span>
                  </button>
                  <button 
                    onClick={() => setCurrentView('rejected')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      currentView === 'rejected' 
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <XCircle className="h-5 w-5" />
                    <span>{p('status_rejected')}</span>
                  </button>
                </div>
              </div>

              {/* Quick Filter Buttons - DYNAMIC with DELETE */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {/* "All" button */}
                  <button 
                    onClick={() => setTypeFilter('all')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                      typeFilter === 'all'
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <span className="hidden sm:inline">{p('all_types')}</span>
                    <span className="sm:hidden">{t('common.all')}</span>
                  </button>
                  
                  {/* Dynamic type buttons */}
                  {absenceTypeOptions.map((option) => (
                    <div key={option.value} className="relative flex-1">
                      <button 
                        onClick={() => setTypeFilter(option.value)}
                        className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                          typeFilter === option.value
                            ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' 
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        } ${!option.isDefault ? 'pr-8' : ''}`}
                      >
                        <span className="hidden sm:inline">{option.label}</span>
                        <span className="sm:hidden">{option.label.split(' ')[0]}</span>
                      </button>
                      
                      {/* X button for user-created types */}
                      {!option.isDefault && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAbsenceType(option.value);
                          }}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-red-500/30 transition-colors group"
                          title={p('delete_type_title', { type: getAbsenceTypeLabel(option.value) })}
                        >
                          <X size={14} className="text-white/50 group-hover:text-red-300" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Distribution Overview */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distribution_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('status_approved')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-green-500 text-white force-white">
                        {stats.approved}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-green-500" style={{ width: `${stats.total > 0 ? (stats.approved / stats.total * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_absences', { percent: stats.total > 0 ? ((stats.approved / stats.total * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('status_pending')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-orange-500 text-white force-white">
                        {stats.pending}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-orange-500" style={{ width: `${stats.total > 0 ? (stats.pending / stats.total * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_absences', { percent: stats.total > 0 ? ((stats.pending / stats.total * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('status_rejected')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-red-500 text-white force-white">
                        {absences.filter(a => a.status === 'rejected').length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-red-500" style={{ width: `${stats.total > 0 ? (absences.filter(a => a.status === 'rejected').length / stats.total * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_absences', { percent: stats.total > 0 ? ((absences.filter(a => a.status === 'rejected').length / stats.total * 100).toFixed(1)) : 0 })}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Table with Integrated Header */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">

        

        

        {/* Table or Card View */}
        {viewMode === 'table' ? (
          // Table View
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('employee_name') && (
                    <TableColumnHeaderFilter columnKey="employee_name" label={p('col_employee')} value={colFilters.employee_name ?? ''} onChange={(v) => setColFilter('employee_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('type') && (
                    <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={colFilters.type ?? ''} onChange={(v) => setColFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('start_date') && (
                    <TableColumnHeaderFilter columnKey="start_date" label={p('col_start_date')} value={colFilters.start_date ?? ''} onChange={(v) => setColFilter('start_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('end_date') && (
                    <TableColumnHeaderFilter columnKey="end_date" label={p('col_end_date')} value={colFilters.end_date ?? ''} onChange={(v) => setColFilter('end_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('duration') && (
                    <TableColumnHeaderFilter columnKey="duration" label={p('col_duration')} value={colFilters.duration ?? ''} onChange={(v) => setColFilter('duration', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('penalty') && (
                    <TableColumnHeaderFilter columnKey="penalty" label={p('table_penalty')} value={colFilters.penalty ?? ''} onChange={(v) => setColFilter('penalty', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {filteredAbsences.length > 0 ? (
                  filteredAbsences.map((absence) => (
                    <tr key={absence.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(absence.id)}
                        onChange={() => handleSelectRecord(absence.id)}
                      />
                      {isColVisible('employee_name') && (
                        <td className="px-6 py-4 text-white font-semibold">{resolvePointageEmployeeDisplayName(absence.employee_id, employeeOptions, absence.employee_name || absence.employeeName)}</td>
                      )}
                      {isColVisible('type') && (
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium">
                            {getAbsenceTypeLabel(absence.type)}
                          </span>
                        </td>
                      )}
                      {isColVisible('start_date') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(absence.start_date || absence.startDate)}</td>
                      )}
                      {isColVisible('end_date') && (
                        <td className="px-6 py-4 text-white/80">{formatPointageDate(absence.end_date || absence.endDate)}</td>
                      )}
                      {isColVisible('duration') && (
                        <td className="px-6 py-4 text-white font-medium">{formatDuration(absence.duration)}</td>
                      )}
                      {isColVisible('penalty') && (
                        <td className="px-6 py-4">
                          {getAbsencePenaltyDisplay(absence) ? (
                            <span className="text-orange-300 text-sm font-medium">{getAbsencePenaltyDisplay(absence)}</span>
                          ) : (
                            <span className="text-white/30 text-xs">—</span>
                          )}
                        </td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={absence.status}
                              recordId={absence.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => handleView(absence)}
                            onEdit={() => handleEdit(absence)}
                            onDelete={() => handleDelete(absence.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="px-6 py-12 text-center text-white/50">
                      {p('empty_no_absences')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          // Card View
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredAbsences.map((absence) => (
                <motion.div
                  key={absence.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl shadow-black/10 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-medium">
                          {getAbsenceTypeLabel(absence.type)}
                        </span>
                        {getStatusBadge(absence.status)}
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-1 truncate">{absence.employee_name || absence.employeeName}</h3>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(absence)}
                        onEdit={() => handleEdit(absence)}
                        onDelete={() => handleDelete(absence.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('card_start_date')}</span>
                      <span className="text-white font-semibold">{formatPointageDate(absence.start_date || absence.startDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs">{p('card_end_date')}</span>
                      <span className="text-white font-semibold">{formatPointageDate(absence.end_date || absence.endDate)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-white/50 text-xs">{p('card_duration')}</span>
                      <span className="text-white font-semibold text-lg">{formatDuration(absence.duration)}</span>
                    </div>
                  </div>

                  {absence.reason && (
                    <div className="bg-white/5 rounded-lg p-3 mt-4">
                      <p className="text-white/50 text-xs mb-1">{p('card_reason')}</p>
                      <p className="text-white text-sm">{absence.reason}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal — coquille « Ajouter un Pointage » */}
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={selectedAbsence ? p('modal_edit_title') : p('modal_new_title')}
          icon={UserX}
          onSubmit
          formId="absence-form"
          saveLabel={selectedAbsence ? p('update') : t('common.save')}
          maxWidth="1000px"
          testId="absences-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={[]}
            activeTab=""
            onTabChange={() => {}}
            formId="absence-form"
            onSubmit={handleSubmit}
            ariaLabel={selectedAbsence ? p('modal_edit_title') : p('modal_new_title')}
          >
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_employee')} />}</label>
                  <CustomSelect
                    value={formData.employeeId ? String(formData.employeeId) : ''}
                    onChange={(v) => {
                      const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(v, employeeOptions);
                      setFormData({ ...formData, employeeId, employeeName });
                    }}
                    options={employeeOptions}
                    placeholder={p('select_create_placeholder')}
                    searchable={true}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_absence_type')} />}</label>
                  <CustomSelect
                    value={formData.type}
                    onChange={(v) => setFormData({ ...formData, type: v })}
                    options={absenceTypeOptions}
                    placeholder={p('select_create_placeholder')}
                    searchable={true}
                    creatable={true}
                    onDeleteOption={handleDeleteAbsenceType}
                    autoSelect={false}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_start_date')} />}</label>
                  <CustomDatePicker
                    value={formData.startDate}
                    onChange={(v) => setFormData({ ...formData, startDate: v })}
                    placeholder={p('date_start_placeholder')}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{<RequiredLabelText text={p('label_end_date')} />}</label>
                  <CustomDatePicker
                    value={formData.endDate}
                    onChange={(v) => setFormData({ ...formData, endDate: v })}
                    placeholder={p('date_end_placeholder')}
                    minDate={formData.startDate ? new Date(`${formData.startDate}T12:00:00`) : undefined}
                  />
                </div>
                <div>
                  <label className={modalForm.label}>{p('label_status')}</label>
                  <CustomSelect
                    value={formData.status}
                    onChange={(v) => setFormData({ ...formData, status: v })}
                    options={statusOptions}
                    placeholder={p('status_placeholder')}
                    autoSelect={false}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_reason')}</label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={4}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('reason_placeholder')}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className={modalForm.label}>{p('label_attachments_notes')}</label>
                  <textarea
                    value={formData.attachmentsNotes || ''}
                    onChange={(e) => setFormData({ ...formData, attachmentsNotes: e.target.value })}
                    rows={4}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none placeholder:text-white/40"
                    placeholder={p('attachments_placeholder')}
                  />
                </div>
              </div>
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}

      {/* View Modal */}
      {showViewModal && selectedAbsence && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="640px"
          testId="absences-view-modal"
        >
          <div className="space-y-4 space-y-4">
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('view_employee')}</p>
                <p className="text-white font-semibold text-lg">{selectedAbsence.employee_name || selectedAbsence.employeeName}</p>
              </div>
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{p('view_type')}</p>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-sm font-medium">{getAbsenceTypeLabel(selectedAbsence.type)}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('view_start_date')}</p>
                  <p className={modalForm.viewValue}>{formatPointageDate(selectedAbsence.start_date || selectedAbsence.startDate)}</p>
                </div>
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('view_end_date')}</p>
                  <p className={modalForm.viewValue}>{formatPointageDate(selectedAbsence.end_date || selectedAbsence.endDate)}</p>
                </div>
              </div>
              <div className={modalForm.viewCard}>
                <p className={modalForm.viewLabel}>{p('view_duration')}</p>
                <p className="text-white font-bold text-2xl">{formatDuration(selectedAbsence.duration)}</p>
              </div>
              {getAbsencePenaltyDisplay(selectedAbsence) && (
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('view_penalty')}</p>
                  <p className="text-orange-300 font-bold text-xl">{getAbsencePenaltyDisplay(selectedAbsence)}</p>
                  <p className="text-white/40 text-xs mt-1">{p('penalty_max_hint', { days: SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxAbsencePenaltyDays })}</p>
                </div>
              )}
              {selectedAbsence.reason && (
                <div className={modalForm.viewCard}>
                  <p className={modalForm.viewLabel}>{p('view_reason')}</p>
                  <p className="text-white text-sm">{selectedAbsence.reason}</p>
                </div>
              )}
              <div className={modalForm.viewCard}>
                <p className="text-white/60 text-sm mb-2">{p('view_status')}</p>
                {getStatusBadge(selectedAbsence.status)}
              </div>
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/20 transition-all"
              >
                {t('common.close')}
              </button>
            </div>
        </QawaemErpViewModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default AbsencesPage;


